import React from "react";
import {
  Briefcase,
  ClipboardList,
  Cpu,
  FileText,
  FolderKanban,
  TrendingUp,
  Clock,
  CheckCircle2,
} from "lucide-react";
import HeaderMenu from "../../component/Navbar/headermenu";
import CustomFooter from "../../component/Footer/customFooter";
import ShowcaseImageLightbox from "../../component/ShowcaseImageLightbox";
import "./about.css";
import taskTable from "../../assets/Consulting/con-tasks.png";
import peekView from "../../assets/Consulting/con-peekview.png";
import fullPageView from "../../assets/Consulting/con-fullpageview.png";
import clientDocuments from "../../assets/Consulting/con-documents.png";

const features = [
  {
    icon: <ClipboardList size={26} />,
    title: "Client Deliverables",
    description:
      "Consulting reports, presentations, and handover meetings tracked as tasks with client, status, and due date — from first draft to final sign-off.",
  },
  {
    icon: <FolderKanban size={26} />,
    title: "Project Management",
    description:
      "Milestones, risk reviews, and dependency tracking across every engagement — so project leads always know what's blocked and what's next.",
  },
  {
    icon: <Cpu size={26} />,
    title: "Digital Transformation",
    description:
      "Technology assessments, software evaluations, and implementation roadmaps organized by client and project, not scattered across spreadsheets.",
  },
  {
    icon: <TrendingUp size={26} />,
    title: "Financial Consulting",
    description:
      "Cost optimization studies, financial analysis, and advisory deliverables tracked with period, financial year, and clear ownership.",
  },
];

const showcase = [
  {
    eyebrow: "Every Engagement, One Task Board",
    title: "Client deliverables, digital transformation, and financial consulting — tracked in one place",
    description:
      "Client Deliverables, Project Management, Digital Transformation, and Financial Consulting — every engagement for Global Retail, FinEdge Group, Sunrise Industries, TechNova Solutions, and Acme Corporation tracked with status, period, financial year, and assignee in one board.",
    image: taskTable,
    alt: "Simplifyte task table showing consulting engagements across Client Deliverables, Project Management, Digital Transformation, and Financial Consulting with client, status, and assignee columns",
  },
  {
    eyebrow: "Every Deliverable, With Full Context",
    title: "Open any task without losing the bigger picture",
    description:
      "A task like 'Review presentation with project lead' opens in a side peek panel with its client, financial year, due date, assignee, and logged time — full context, without leaving the task list behind.",
    image: peekView,
    alt: "Simplifyte task peek panel showing Review presentation with project lead for Acme Corporation with financial year, due date, assignee, and time log",
  },
  {
    eyebrow: "Deliverable Detail",
    title: "Client, period, due date, and billable time — all on one page",
    description:
      "Every consulting deliverable carries its client, financial year, period, due date, assignee, and billable time log — so partners and project leads have everything they need for reviews and invoicing, without switching tools.",
    image: fullPageView,
    alt: "Simplifyte full task view showing Review presentation with project lead under Client Deliverables for Acme Corporation with period, due date, assignee, and 2h time log",
  },
  {
    eyebrow: "Client Documents",
    title: "Every client's engagement files, organized by account",
    description:
      "Crestview Hospitality, Prime Industries, Urban Developers, TechNova Solutions — each client gets its own document folder with upload tracking, client visibility controls, and a clear record of who shared what and when.",
    image: clientDocuments,
    alt: "Simplifyte Client Documents screen showing consulting client folders for Crestview Hospitality, Prime Industries, Urban Developers, and TechNova Solutions with upload and share details",
  },
];

const benefits = [
  "Track every client engagement and deliverable in one board",
  "See client, period, and assignee context without status calls",
  "Log billable time directly on each consulting task",
  "Keep client documents organized by account, not email threads",
  "Never miss a reporting or handover deadline again",
];

const ConsultingAgenciesPage = () => {
  return (
    <>
      <HeaderMenu />
      <div className="Consulting_class_main">
        {/* Hero */}
        <section className="Consulting_class_hero">
          <div className="Consulting_class_container">
            <div className="Consulting_class_hero_content">
              <div className="Consulting_class_icon_box">
                <Briefcase size={34} />
              </div>

              <h1 className="Consulting_class_heading">
                Client engagement software for Consulting agencies
              </h1>

              <p className="Consulting_class_subheading">
                Track client deliverables, project milestones, billable time,
                and engagement documents in one place your whole team can trust.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="Consulting_class_features_section">
          <div className="Consulting_class_container">
            <h2 className="Consulting_class_section_title Consulting_class_center">
              Everything a consulting firm's back office needs
            </h2>

            <div className="Consulting_class_features_grid">
              {features.map((feature, index) => (
                <div key={index} className="Consulting_class_feature_card">
                  <div className="Consulting_class_feature_icon">
                    {feature.icon}
                  </div>

                  <h3 className="Consulting_class_feature_title">
                    {feature.title}
                  </h3>

                  <p className="Consulting_class_feature_description">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Showcase */}
        <section className="Consulting_class_showcase_section">
          <div className="Consulting_class_container">
            <h2 className="Consulting_class_section_title Consulting_class_center">
              Built for the pace of client engagements
            </h2>
            <p className="Consulting_class_showcase_subtitle">
              Real screens from the product — task boards, deliverable detail,
              time logging, and client documents your team can rely on.
            </p>

            <div className="Consulting_class_showcase_list">
              {showcase.map((item, index) => (
                <div
                  key={index}
                  className={`Consulting_class_showcase_row ${
                    index % 2 === 1
                      ? "Consulting_class_showcase_row_reverse"
                      : ""
                  }`}
                >
                  <div className="Consulting_class_showcase_text">
                    <span className="Consulting_class_showcase_eyebrow">
                      {item.eyebrow}
                    </span>
                    <h3 className="Consulting_class_showcase_title">
                      {item.title}
                    </h3>
                    <p className="Consulting_class_showcase_description">
                      {item.description}
                    </p>
                  </div>

                  <div className="Consulting_class_showcase_media">
                    <div className="Consulting_class_showcase_frame_outer">
                      <div className="Consulting_class_showcase_frame_inner">
                        <ShowcaseImageLightbox
                          src={item.image}
                          alt={item.alt}
                          className="Consulting_class_showcase_image"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="Consulting_class_benefits_section">
          <div className="Consulting_class_container">
            <div className="Consulting_class_benefits_wrapper">
              <h2 className="Consulting_class_section_title Consulting_class_center">
                Why consulting agencies choose Simplifyte
              </h2>

              <div className="Consulting_class_benefits_list">
                {benefits.map((benefit, index) => (
                  <div key={index} className="Consulting_class_benefit_item">
                    <CheckCircle2
                      size={24}
                      className="Consulting_class_check_icon"
                    />

                    <p>{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="Consulting_class_cta_section">
          <div className="Consulting_class_container">
            <div className="Consulting_class_cta_box">
              <h2 className="Consulting_class_cta_title">
                Ready to bring structure to client engagements?
              </h2>

              <p className="Consulting_class_cta_text">
                Join consulting teams who've gained full visibility across
                every account, deliverable, and billable hour.
              </p>

              <button
                className="Consulting_class_cta_button"
                onClick={() =>
                  (window.location.href = "https://app.simplifyte.com")
                }
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </section>
      </div>
      <CustomFooter />
    </>
  );
};

export default ConsultingAgenciesPage;
